import React, { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  CheckIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  ShoppingBagIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  ClockIcon,
  TruckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import {
  PRODUCTS,
  QUANTITY_OPTIONS,
  SUBSCRIPTION_OPTIONS,
  UNITS_PER_BOX,
  getShippingInfo,
} from '../data/products'
import { useCart } from '../context/CartContext'
import BulkOffersBanner from '../components/BulkOffersBanner'
import PriceBanner from '../components/PriceBanner'
import api from '../config/api'

const ProductDetails = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { addToCart } = useCart()

  const [product, setProduct] = useState(PRODUCTS.find(p => p.slug === slug))
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchSingleProduct = async () => {
      try {
        const response = await api.get('/admin_api.php?action=get_products')
        if (response.data.success && response.data.data) {
          const found = response.data.data.find(p => p.slug === slug)
          if (found) {
            setProduct(found)
          }
        }
      } catch (err) {
        console.warn('Could not fetch product details from database, using static fallback.', err)
      } finally {
        setLoading(false)
      }
    }
    fetchSingleProduct()
  }, [slug])

  const [selectedQty, setSelectedQty] = useState(QUANTITY_OPTIONS[0].value)
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState(SUBSCRIPTION_OPTIONS[1]) // 3 months default
  const [engravingFile, setEngravingFile] = useState(null)
  const [engravingInstructions, setEngravingInstructions] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [addedToCart, setAddedToCart] = useState(false)
  const [activeImage, setActiveImage] = useState(0)
  const fileInputRef = useRef(null)

  useEffect(() => {
    // Reset all product states when product slug changes
    setActiveImage(0)
    setSelectedQty(QUANTITY_OPTIONS[0].value)
    setSubscriptionEnabled(false)
    setSelectedSubscription(SUBSCRIPTION_OPTIONS[1])
    setEngravingFile(null)
    setEngravingInstructions('')
    setUploadSuccess(false)
    setAddedToCart(false)

    // Instantly reset scroll to top
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    document.body.scrollTo({ top: 0, left: 0, behavior: 'instant' })

    // Secondary reset timer to catch any async render or scroll-restoration ticks
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      document.body.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }, 20)

    return () => clearTimeout(timer)
  }, [slug])

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Product Not Found</h1>
        <p className="text-gray-500 mb-6">We couldn't find the product you're looking for.</p>
        <Link to="/shop" className="px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors">
          Back to Shop
        </Link>
      </div>
    )
  }

  const [selectedOptions, setSelectedOptions] = useState({})

  useEffect(() => {
    if (product && product.options && product.options.length > 0) {
      const defaults = {}
      const grouped = product.options.reduce((acc, opt) => {
        if (!acc[opt.name]) acc[opt.name] = []
        acc[opt.name].push(opt)
        return acc
      }, {})
      Object.keys(grouped).forEach(name => {
        defaults[name] = grouped[name][0]
      })
      setSelectedOptions(defaults)
    } else {
      setSelectedOptions({})
    }
  }, [product])

  const shipping = getShippingInfo(selectedQty, subscriptionEnabled)
  const optionsPriceModifier = Object.values(selectedOptions).reduce((sum, opt) => sum + (opt.price_modifier || 0), 0)
  const lineTotal = (product.pricePerBox + optionsPriceModifier) * selectedQty

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const allowed = ['image/png', 'image/jpeg', 'application/pdf', 'image/svg+xml']
    if (!allowed.includes(file.type)) {
      alert('Please upload a PNG, JPG, SVG or PDF file.')
      return
    }
    setEngravingFile(file)
    setUploadSuccess(true)
  }

  const removeFile = () => {
    setEngravingFile(null)
    setUploadSuccess(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleAddToCart = () => {
    addToCart(
      product,
      selectedQty,
      engravingFile ? { file: engravingFile, instructions: engravingInstructions } : null,
      subscriptionEnabled ? selectedSubscription : null,
      selectedOptions
    )
    setAddedToCart(true)
    setTimeout(() => setAddedToCart(false), 2500)
  }

  const otherProducts = PRODUCTS.filter(p => p.id !== product.id && p.category === product.category).slice(0, 2)

  return (
    <div className="min-h-screen bg-white">

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <ChevronRightIcon className="w-4 h-4 mx-2 text-gray-300" />
            <Link to="/shop" className="hover:text-primary-600 transition-colors">Shop</Link>
            <ChevronRightIcon className="w-4 h-4 mx-2 text-gray-300" />
            <span className="text-gray-900 font-medium">{product.fullName}</span>
          </nav>
        </div>
      </div>

      <PriceBanner className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" />
      <BulkOffersBanner className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4" />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-16">

          {/* ─ Left: Images ─ */}
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-2xl overflow-hidden h-96 lg:h-[500px] flex items-center justify-center p-8 border border-gray-100">
              <img
                src={product.images[activeImage]}
                alt={product.fullName}
                className="max-h-full max-w-full object-contain"
                loading="eager"
              />
            </div>
            {product.images.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`w-20 h-20 rounded-xl border-2 overflow-hidden bg-gray-50 flex items-center justify-center p-2 transition-all duration-200 ${
                      activeImage === i ? 'border-primary-500 shadow-md' : 'border-gray-100 hover:border-gray-300'
                    }`}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={img} alt={`View ${i + 1}`} className="max-h-full max-w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ─ Right: Details ─ */}
          <div className="space-y-8">
            {/* Category badge */}
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                product.category === 'kids'
                  ? 'bg-pink-100 text-pink-700'
                  : 'bg-primary-100 text-primary-700'
              }`}>
                {product.category === 'kids' ? 'Kids' : 'Adult'}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                Eco-Friendly
              </span>
            </div>

            {/* Title */}
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900">{product.fullName}</h1>
              <p className="mt-3 text-gray-600 text-base leading-relaxed">{product.description}</p>
            </div>

            {/* Price */}
            <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
              <div className="flex flex-col gap-2">
                <div className="flex items-end gap-2">
                  <span className="text-4xl font-extrabold text-gray-900">${product.pricePerBox.toFixed(2)}</span>
                  <span className="text-gray-500 text-base mb-1">/ box ({UNITS_PER_BOX} toothbrushes)</span>
                </div>
                <div className="text-xs text-gray-500 space-y-1 border-t border-gray-200 pt-3 mt-1">
                  {product.category === 'adult' ? (
                    <p className="flex justify-between">
                      <span>Adult brushes: 1 box of {UNITS_PER_BOX}</span>
                      <span>${product.pricePerBox.toFixed(0)}</span>
                    </p>
                  ) : (
                    <p className="flex justify-between">
                      <span>Kids brushes: 1 box of {UNITS_PER_BOX} brushes</span>
                      <span>${product.pricePerBox.toFixed(0)}</span>
                    </p>
                  )}
                  <p className="font-semibold text-primary-600">Optional: Engraving extra $10 per box (free on 5+ boxes)</p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 text-sm">
                <span className="flex items-center gap-1.5 text-gray-500">
                  <ClockIcon className="w-4 h-4" />
                  {product.deliveryTimeline}
                </span>
                <span className={`flex items-center gap-1.5 font-medium ${shipping.cost === 0 ? 'text-green-600' : 'text-gray-500'}`}>
                  <TruckIcon className="w-4 h-4" />
                  {shipping.label}
                </span>
              </div>
              {shipping.cost > 0 && (
                <p className="text-xs text-gray-400 mt-1">{shipping.note}</p>
              )}
            </div>

            {/* ─── Dynamic Product Options ─── */}
            {product.options && product.options.length > 0 && (
              <div className="space-y-4 bg-gray-50 p-5 rounded-2xl border border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Customize Product Options</h3>
                <div className="grid grid-cols-1 gap-4">
                  {Object.entries(
                    product.options.reduce((acc, opt) => {
                      if (!acc[opt.name]) acc[opt.name] = []
                      acc[opt.name].push(opt)
                      return acc
                    }, {})
                  ).map(([optionName, optionValues]) => (
                    <div key={optionName} className="space-y-1.5">
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">{optionName}</label>
                      <div className="flex flex-wrap gap-2">
                        {optionValues.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setSelectedOptions(prev => ({ ...prev, [optionName]: opt }))}
                            className={`px-4 py-2 text-xs font-semibold rounded-xl border transition-all duration-200 ${
                              selectedOptions[optionName]?.id === opt.id
                                ? 'bg-primary-600 border-primary-600 text-white shadow-sm'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-700'
                            }`}
                          >
                            {opt.value}
                            {opt.price_modifier > 0 && ` (+ $${opt.price_modifier.toFixed(2)})`}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ─── Quantity Selector ─── */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Order Quantity</h3>
              <div className="grid grid-cols-2 gap-3">
                {QUANTITY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    id={`qty-${opt.value}`}
                    onClick={() => setSelectedQty(opt.value)}
                    className={`p-3.5 rounded-xl border-2 text-sm font-semibold text-left transition-all duration-200 ${
                      selectedQty === opt.value
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="block text-base font-extrabold">{opt.value} {opt.value === 1 ? 'box' : 'boxes'}</span>
                    <span className="text-xs font-normal text-gray-500">{opt.value * UNITS_PER_BOX} toothbrushes</span>
                  </button>
                ))}
              </div>

              {/* Line total */}
              <div className="mt-4 flex items-center justify-between text-sm font-medium bg-primary-50 px-4 py-3 rounded-xl border border-primary-100">
                <span className="text-gray-600">
                  {selectedQty} box{selectedQty > 1 ? 'es' : ''} × ${product.pricePerBox.toFixed(2)}
                </span>
                <span className="text-primary-700 font-extrabold text-base">${lineTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* ─── Subscription ─── */}
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              <div className="bg-gray-50 px-5 py-4 flex items-center gap-3">
                <input
                  id="subscription-toggle"
                  type="checkbox"
                  checked={subscriptionEnabled}
                  onChange={e => setSubscriptionEnabled(e.target.checked)}
                  className="w-4 h-4 accent-primary-600 cursor-pointer"
                />
                <label htmlFor="subscription-toggle" className="text-sm font-bold text-gray-900 cursor-pointer flex items-center gap-2">
                  <SparklesIcon className="w-4 h-4 text-primary-600" />
                  Subscribe & Save (Recurring Delivery)
                </label>
              </div>

              {subscriptionEnabled && (
                <div className="px-5 py-4 space-y-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">Choose your delivery frequency:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {SUBSCRIPTION_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        id={`sub-${opt.value}`}
                        onClick={() => setSelectedSubscription(opt)}
                        className={`p-2.5 rounded-lg border text-xs font-semibold text-center transition-all duration-200 ${
                          selectedSubscription.value === opt.value
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                    <p className="text-xs text-green-700 font-medium">
                      You'll receive <strong>{selectedQty} box{selectedQty > 1 ? 'es' : ''}</strong> of <strong>{product.name}</strong> {selectedSubscription.label.toLowerCase()}.
                      Cancel anytime.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* ─── Custom Engraving ─── */}
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              <div className="bg-gray-50 px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-900">Custom Logo Engraving <span className="text-gray-400 font-normal">(Optional)</span></h3>
                <p className="text-xs text-gray-500 mt-1">
                  Upload your clinic logo to have it laser-engraved on every toothbrush handle in this order.
                </p>
              </div>

              <div className="px-5 py-5 space-y-4">
                {/* Upload area */}
                {!uploadSuccess ? (
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-primary-400 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      e.preventDefault()
                      const file = e.dataTransfer.files[0]
                      if (file) {
                        const fakeEvent = { target: { files: [file] } }
                        handleFileUpload(fakeEvent)
                      }
                    }}
                  >
                    <CloudArrowUpIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700">Upload Logo / Design</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, SVG, PDF accepted</p>
                    <button
                      id="engraving-upload-btn"
                      type="button"
                      className="mt-3 px-4 py-2 text-xs font-semibold text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors"
                    >
                      Browse Files
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg,.pdf"
                      className="hidden"
                      onChange={handleFileUpload}
                      id="engraving-file-input"
                      aria-label="Upload clinic logo for engraving"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-green-800">Logo uploaded successfully</p>
                      <p className="text-xs text-green-600 truncate mt-0.5">{engravingFile?.name}</p>
                    </div>
                    <button
                      onClick={removeFile}
                      className="p-1 text-green-400 hover:text-red-500 transition-colors"
                      aria-label="Remove uploaded file"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Instructions */}
                <div>
                  <label htmlFor="engraving-instructions" className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Engraving Instructions <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    id="engraving-instructions"
                    rows={3}
                    value={engravingInstructions}
                    onChange={e => setEngravingInstructions(e.target.value)}
                    placeholder="e.g. Please engrave our clinic logo on the toothbrush handle."
                    className="w-full text-sm rounded-xl border border-gray-200 px-4 py-3 text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none resize-none transition-colors"
                  />
                </div>

                {!engravingFile && (
                  <p className="text-xs text-gray-400 text-center">
                    Skip this section if you don't want custom engraving.
                  </p>
                )}
              </div>
            </div>

            {/* Add to Cart */}
            <div className="space-y-3">
              <button
                id={`add-to-cart-${product.slug}`}
                onClick={handleAddToCart}
                className={`w-full flex items-center justify-center gap-2.5 py-4 px-8 rounded-xl font-bold text-base transition-all duration-300 ${
                  addedToCart
                    ? 'bg-green-500 text-white'
                    : 'bg-primary-600 hover:bg-primary-700 text-white hover:scale-[1.01] shadow-lg hover:shadow-primary-900/30'
                }`}
              >
                <ShoppingBagIcon className="w-5 h-5" />
                {addedToCart ? 'Added to Cart!' : `Add ${selectedQty} Box${selectedQty > 1 ? 'es' : ''} to Cart`}
              </button>
              <p className="text-center text-xs text-gray-400">
                Minimum 1 box = {UNITS_PER_BOX} toothbrushes. Shipping across all of Canada.
              </p>
            </div>
          </div>
        </div>

        {/* ─── Product Details Info ─── */}
        <div className="mt-16 border-t border-gray-100 pt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Why Clinics Choose Them */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
              <span className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
                <SparklesIcon className="w-3.5 h-3.5 text-primary-600" />
              </span>
              Why Clinics Choose Us
            </h2>
            <ul className="space-y-3">
              {product.category === 'kids' ? (
                <>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-primary-500 font-bold">•</span> Perfect for pediatric patients
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-primary-500 font-bold">•</span> Eco-conscious and sustainable
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-primary-500 font-bold">•</span> Ready for bulk stocking
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-primary-500 font-bold">•</span> Eco-conscious choice for patients
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-primary-500 font-bold">•</span> Comfortable and safe for daily use
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-primary-500 font-bold">•</span> Ready for bulk stocking
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Features */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
              <span className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
                <CheckIcon className="w-3.5 h-3.5 text-primary-600" />
              </span>
              Key Features
            </h2>
            <ul className="space-y-3">
              {product.features.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 w-5 h-5 bg-primary-50 rounded-full flex items-center justify-center">
                    <CheckIcon className="w-3 h-3 text-primary-600" />
                  </span>
                  <span className="text-sm text-gray-700">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Packaging Info */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
              <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                <ShoppingBagIcon className="w-3.5 h-3.5 text-amber-600" />
              </span>
              Packaging Information
            </h2>
            <ul className="space-y-3 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <span className="text-primary-500">•</span>
                <strong>{product.packaging.unitsPerBox} toothbrushes</strong> per box
              </li>
              <li className="flex items-center gap-2">
                <span className="text-primary-500">•</span>
                {product.packaging.packagingType}
              </li>
              {product.packaging.bulkAvailable && (
                <li className="flex items-center gap-2">
                  <span className="text-primary-500">•</span>
                  Bulk packaging available for clinics
                </li>
              )}
            </ul>
          </div>

          {/* Shipping Info */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
              <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <TruckIcon className="w-3.5 h-3.5 text-blue-600" />
              </span>
              Shipping & Delivery
            </h2>
            <ul className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-primary-500 mt-1">•</span>
                <span>
                  <strong>Free shipping</strong> on orders of 2+ boxes across Canada (otherwise $15.00 shipping fee)
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-primary-500">•</span>
                Delivery: <strong>3–4 weeks</strong> after confirmation
              </li>
            </ul>
          </div>
        </div>

        {/* ─── Related Products ─── */}
        {otherProducts.length > 0 && (
          <div className="mt-16 border-t border-gray-100 pt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-7">You Might Also Like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl">
              {otherProducts.map(p => (
                <div
                  key={p.id}
                  className="flex items-center gap-4 p-4 border border-gray-100 rounded-2xl hover:shadow-sm transition-all cursor-pointer group"
                  onClick={() => navigate(`/shop/${p.slug}`)}
                >
                  <div className="w-20 h-20 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0 p-2">
                    <img src={p.image} alt={p.fullName} className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">{p.fullName}</p>
                    <p className="text-primary-600 font-bold mt-0.5">${p.pricePerBox.toFixed(2)} / box</p>
                    <span className="text-xs text-primary-600 flex items-center gap-1 mt-1">
                      View Product <ChevronRightIcon className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductDetails
